import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import DrawerComponent from '../../components/DrawerComponent';
import LoaderSmall from '../../components/LoaderSmall';
import PaginationComponent from '../../components/PaginationComponent';
import { useApi } from '../../components/ApiProvider';
import authQueries from '../../queries/authQueries';
import dropdownQueries from '../../queries/dropdownQueries';
import { Search, User, Mail, Phone, Edit, ArrowRight, ChevronDown, Filter } from 'lucide-react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@nextui-org/react';
import { Clock, Check, Crown, XCircle, Sparkles, X } from 'lucide-react';
import moment from 'moment';
import useDebounce from '../../hooks/useDebounce';
import { setPage } from '../../redux/reducers/paginationReducers';

const UsersList = () => {

    const dispatch = useDispatch()

    const [result, setResult] = useState([]);
    const [roles, setRoles] = useState([]);
    const [search, setSearch] = useState("");
    const [subscription, setSubscription] = useState("");
    const [loading, setLoading] = useState(true)

    const currentPage = useSelector((state) => state.pagination.currentPage);

    const inputFields = [
        {
            name: 'username',
            type: 'text',
            label: 'Username',
            required: true
        },
        {
            name: 'fname',
            type: 'text',
            label: 'First Name',
            required: true
        },
        {
            name: 'lname',
            type: 'text',
            label: 'Last Name',
            required: true
        },
        {
            name: 'email',
            type: 'email',
            label: 'Email',
            required: false
        },
        {
            name: 'phone',
            type: 'text',
            label: 'Mobile Number',
            required: false
        },
        {
            name: 'role_id',
            type: 'select',
            label: 'Role',
            options: roles,
            required: true
        }
    ];

    const { apiData, setApiDataAndNotify } = useApi();

    const userAddorEdit = dropdownQueries.useUserAddOrEditMutation(
        (res) => {
            setRoles(res?.roles);
        }
    );

    useEffect(() => {
        userAddorEdit.mutateAsync({});
    }, []);

    const userList = authQueries.useUserListMutation(
        (res) => {
          setLoading(false)
          setResult(res);
        }
    );

    const debouncedSearch = useDebounce(search, 400);
    
    useEffect(() => {
        userList.mutateAsync({ page: currentPage, size: 25, search: search, subscription: subscription });
    }, [apiData, currentPage, debouncedSearch, subscription]);

    const updateUser = authQueries.useUpdateUserMutation(
        (res) => {
            setApiDataAndNotify(res);
            toast.success('User updated successfully');
        }
    );

    const handleSubscription = (item, status) => {
      updateSubscription.mutateAsync({id: item?.id, subscription: status || "Trial"});
    }

    const updateSubscription = authQueries.useSubscriptionStatusChange(
      (res)=>{
        setApiDataAndNotify(res);
        toast.success('Subscription updated successfully');
      }
    )


  return (
    <div className="rounded-xl overflow-hidden backdrop-blur-sm">
      <div className="bg-white bg-opacity-90 rounded-xl overflow-hidden shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] border border-gray-200/50 backdrop-filter backdrop-blur-xl">
        <div className="px-8 pt-6 pb-4 border-b border-gray-100 relative">
          
          <div className="flex flex-col md:flex-row justify-end items-center gap-4">
            <div className="relative w-full md:w-80">
                <select
                type="text"
                placeholder="Search by name, mobile, email"
                className="w-full h-12 pl-12 pr-6 rounded-2xl border-2 border-gray-200/60 bg-white/90 backdrop-blur-sm focus:outline-none focus:border-indigo-400 focus:bg-white shadow-lg transition-all duration-300 text-gray-800 placeholder:text-gray-500"
                value={subscription}
                onChange={(e) => setSubscription(e.target.value)}
                >
                  <option value="">Select Status</option>
                  <option value="Paid">Premium Users</option>
                  <option value="Trial">Trial Users</option>
                  <option value="Expired">Expired Users</option>
                  </select>
                <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
                  <div className="w-8 h-8  bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <Filter className="w-4 h-4 text-white" />
                  </div>
                </div>
                {search && (
                  <button
                  onClick={() => setSearch('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-all duration-200"
                  >
                    <span className="text-gray-500 text-xs">✕</span>
                  </button>
                )}
            </div>
            <div className="relative w-full md:w-80">
                <input
                type="text"
                placeholder="Search by name, mobile, email"
                className="w-full h-12 pl-[50px] pr-6 rounded-2xl border-2 border-gray-200/60 bg-white/90 backdrop-blur-sm focus:outline-none focus:border-indigo-400 focus:bg-white shadow-lg transition-all duration-300 text-gray-800 placeholder:text-gray-500"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                />
                <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
                  <div className="w-8 h-8  bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <Search className="w-4 h-4 text-white" />
                  </div>
                </div>
                {search && (
                  <button
                  onClick={() => setSearch('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-all duration-200"
                  >
                    <span className="text-gray-500 text-xs">✕</span>
                  </button>
                )}
              </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="w-full flex items-center justify-center py-20">
              <LoaderSmall />
            </div>
          ) : (
            <Fragment>
              {result?.data?.totalItems > 0 && (
                <div className="px-8 py-3 flex justify-between items-center bg-gray-50/80 backdrop-blur-sm">
                  <div className="text-sm text-gray-500">
                    Showing {result?.data?.currentPage * 10 + 1} to {result?.data?.currentPage * 10 + result?.data?.items?.length} of {result?.data?.totalItems} entries
                  </div>
                </div>
              )}
              
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100/80 backdrop-blur-sm">
                    <th className="pl-8 pr-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">#</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">User</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">Contact</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">Role</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">Vessel</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">Status</th>
                    <th className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-left">Last subscription date</th>
                    <th className="px-8 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {result?.data?.items?.map((user, index) => (
                    <tr 
                      key={user?.id} 
                      className="hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-transparent transition-colors duration-300"
                    >
                      <td className="pl-8 pr-3 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-500 font-medium">
                            {(index + 1) + (result?.data?.currentPage * 10)}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-9 h-9 bg-gradient-to-br from-indigo-600 to-purple-500 rounded-lg flex items-center justify-center text-white font-medium text-sm shadow-md">
                            {user?.fname?.charAt(0)}{user?.lname?.charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-800">{user?.username}</div>
                            <div className="text-xs text-gray-500">{user?.fname} {user?.lname}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {!user?.email && !user?.email &&
                          "N/A"
                        }
                        <div className="flex flex-col gap-1">
                          {user?.email && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Mail size={14} className="mr-1.5 text-gray-400" /> 
                              <span className="truncate max-w-[180px]">{user?.email}</span>
                            </div>
                          )}
                          {user?.phone && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Phone size={14} className="mr-1.5 text-gray-400" /> 
                              <span>{user?.phone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="px-2.5 py-1 rounded-md bg-indigo-100/70 backdrop-blur-sm border border-indigo-200/50 text-xs font-medium text-indigo-800 w-fit">
                          {user?.role?.name}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="px-2.5 py-1 rounded-md bg-blue-100/70 backdrop-blur-sm border border-blue-200/50 text-xs font-medium text-blue-800 w-fit">
                          {user?.vessel?.name || 'Not Assigned'}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <PremiumSubscriptionUI user={user} handleSubscription={handleSubscription} />
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {
                          user?.last_subscription_date ? 
                          moment(user?.last_subscription_date).format('DD-MM-YYYY') :
                          "-"
                        }
                      </td>
                      <td className="px-8 py-3 whitespace-nowrap text-right">
                        <div className='gap-2 flex items-center justify-end'>
                          <DrawerComponent 
                            data={user} 
                            edit={true} 
                            entityForm={updateUser} 
                            inputFields={inputFields} 
                            btnName={'Update User'} 
                            header={'Update User'} 
                            className="inline-flex items-center gap-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-1.5 px-3 rounded-md text-xs font-medium shadow-sm hover:shadow-md transition-all duration-300"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                  
                  {result?.data?.items?.length === 0 && (
                    <tr>
                      <td colSpan="8  " className="px-4 py-12">
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="w-16 h-16 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-full flex items-center justify-center mb-4">
                            <User size={32} className="text-indigo-500" />
                          </div>
                          <h3 className="text-gray-700 font-medium mb-2">No users found</h3>
                          <p className="text-gray-500 text-sm max-w-md mb-4">
                            We couldn't find any users matching your search criteria
                          </p>
                          <button 
                            onClick={() => setSearch('')}
                            className="flex items-center gap-1 text-indigo-600 text-sm font-medium"
                          >
                            Clear search
                            <ArrowRight size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              
              {/* Pagination */}
              <div className="border-t border-gray-100 p-4 bg-gradient-to-r from-gray-50/80 to-white/60 backdrop-blur-sm">
                <PaginationComponent result={result} />
              </div>
            </Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersList;

const PremiumSubscriptionUI = ({ user, handleSubscription }) => {
  // Status styles
  const getButtonStyle = (status) => {
    switch(status) {
      case 'Trial': return {
        bg: "bg-gradient-to-r from-amber-400 to-orange-400",
        hoverBg: "hover:bg-gradient-to-r hover:from-amber-500 hover:to-orange-500",
        shadow: "shadow-amber-200/30"
      };
      case 'Paid': return {
        bg: "bg-gradient-to-r from-indigo-500 to-violet-600",
        hoverBg: "hover:bg-gradient-to-r hover:from-indigo-600 hover:to-violet-700",
        shadow: "shadow-indigo-200/30"
      };
      case 'Expired': return {
        bg: "bg-gradient-to-r from-rose-400 to-pink-500",
        hoverBg: "hover:bg-gradient-to-r hover:from-rose-500 hover:to-pink-600",
        shadow: "shadow-rose-200/30"
      };
      default: return {
        bg: "bg-gradient-to-r from-amber-400 to-orange-400",
        hoverBg: "hover:bg-gradient-to-r hover:from-amber-500 hover:to-orange-500",
        shadow: "shadow-amber-200/30"
      };
    };
  };
  
  // Status icons
  const getButtonIcon = (status) => {
    switch(status) {
      case 'Trial': return <Clock size={14} className="text-white" />;
      case 'Paid': return <Crown size={14} className="text-white" />;
      case 'Expired': return <XCircle size={14} className="text-white" />;
      default: return <Clock size={14} className="text-white" />;
    }
  };
  
  return (
    <Popover placement="left">
      <PopoverTrigger>
        <Button
          size="sm" 
          className={`${getButtonStyle(user?.subscription).bg} text-white shadow-md hover:shadow-lg transition-all duration-300 h-7`}
        >
          {getButtonIcon(user?.subscription)} {user?.subscription}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 border-0 shadow-xl rounded-xl overflow-hidden">
        <div className="w-48">
          {/* Header */}
          <div className="p-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800">Change Plan</h3>
          </div>
          
          <div className="flex flex-col p-2 gap-2">
            <button 
            onClick={() => handleSubscription(user, 'Trial')}
            className={`relative group overflow-hidden rounded-lg ${getButtonStyle('Trial').bg} ${getButtonStyle('Trial').hoverBg} text-white p-0.5 shadow-md ${getButtonStyle('Trial').shadow} hover:shadow-lg transition-all duration-300`}
            >
              <div className="absolute top-0 left-0 w-12 h-12 rounded-full bg-white/10 blur-xl transform -translate-x-6 -translate-y-6"></div>
              
              <div className="relative z-10 flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <Clock size={12} />
                  </div>
                  <span className="text-sm font-medium">Trial</span>
                </div>
                {user?.subscription === 'Trial' && (
                  <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center">
                    <Check size={10} />
                  </div>
                )}
              </div>
            </button>
            
            <button 
            onClick={() => handleSubscription(user, 'Paid')}
            className={`relative group overflow-hidden rounded-lg ${getButtonStyle('Paid').bg} ${getButtonStyle('Paid').hoverBg} text-white p-0.5 shadow-md ${getButtonStyle('Paid').shadow} hover:shadow-lg transition-all duration-300`}
            >
              <div className="absolute top-0 left-0 w-12 h-12 rounded-full bg-white/10 blur-xl transform -translate-x-6 -translate-y-6"></div>
              
              <div className="relative z-10 flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <Crown size={12} />
                  </div>
                  <span className="text-sm font-medium">Premium</span>
                </div>
                {user?.subscription === 'Paid' && (
                  <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center">
                    <Check size={10} />
                  </div>
                )}
              </div>
            </button>
            
            <button 
            onClick={() => handleSubscription(user, 'Expired')}
            className={`relative group overflow-hidden rounded-lg ${getButtonStyle('Expired').bg} ${getButtonStyle('Expired').hoverBg} text-white p-0.5 shadow-md ${getButtonStyle('Expired').shadow} hover:shadow-lg transition-all duration-300`}
            >
              <div className="absolute top-0 left-0 w-12 h-12 rounded-full bg-white/10 blur-xl transform -translate-x-6 -translate-y-6"></div>
              
              <div className="relative z-10 flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <XCircle size={12} />
                  </div>
                  <span className="text-sm font-medium">Expired</span>
                </div>
                {user?.subscription === 'Expired' && (
                  <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center">
                    <Check size={10} />
                  </div>
                )}
              </div>
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};